// MUI twin of design/ui.tsx FilterGroup. Sidebar filter block: mono label +
// selectable rows, single- or multi-select. Prop API matches the kt original
// verbatim (FilterRow reproduced verbatim).
import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { brainTokens } from "../design/brainTheme";
import { Icon } from "../design/ui";

const c = brainTokens.color;

export interface FilterRow {
  key: string;
  label: ReactNode;
  count?: ReactNode;
  dotColor?: string; // status-dot color (status filters)
  swatchColor?: string; // square swatch color (type legend)
  checked?: boolean; // checkbox state (multi-select tier/type)
}

/** Sidebar filter block: mono label + selectable rows. Single- or multi-select. */
export function FilterGroup({
  label,
  rows,
  mode = "single",
  selected,
  onSelect,
}: {
  label: string;
  rows: FilterRow[];
  mode?: "single" | "multi";
  selected: string | string[];
  onSelect: (key: string) => void;
}) {
  const isActive = (key: string) => (Array.isArray(selected) ? selected.includes(key) : selected === key);
  return (
    <Box sx={{ padding: "6px 22px" }}>
      <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, letterSpacing: "0.1em", color: c.muted, margin: "8px 0 10px" }}>
        {label}
      </Box>
      {rows.map((r) => {
        const active = isActive(r.key);
        const activeSingle = active && mode === "single";
        return (
          <Box
            key={r.key}
            component="button"
            onClick={() => onSelect(r.key)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "7px 10px",
              margin: "0 -10px 1px",
              borderRadius: "6px",
              background: activeSingle ? c.panelBg2 : "none",
              border: "none",
              width: "calc(100% + 20px)",
              textAlign: "left",
              cursor: "pointer",
              fontFamily: brainTokens.font.sans,
              transition: "background .12s ease",
              "&:hover": { background: c.panelBg2 },
            }}
          >
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: "9px", fontSize: 13.5, color: activeSingle ? c.ink : c.muted, fontWeight: activeSingle ? 500 : 400 }}
            >
              {mode === "multi" && r.dotColor == null && r.swatchColor == null && (
                <Icon name={r.checked ? "square-check" : "square"} size={19} color={r.checked ? c.teal : c.faint} />
              )}
              {r.dotColor && (
                <Box component="span" sx={{ width: "7px", height: "7px", borderRadius: "50%", flex: "none", display: "inline-block", background: r.dotColor }} />
              )}
              {r.swatchColor && (
                <Box component="span" sx={{ width: "8px", height: "8px", borderRadius: "2px", flex: "none", background: r.swatchColor }} />
              )}
              {r.label}
            </Box>
            {r.count != null && (
              <Box component="span" sx={{ fontSize: 12.5, color: activeSingle ? c.faint : c.muted, fontWeight: activeSingle ? 500 : 400 }}>
                {r.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
